import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import AnswerStyleEditor from '@/components/answerStyle/AnswerStyleEditor';
import { sameAnswerStyle, type AnswerStyle } from '@/components/chat/AnswerStyle';
import { SaveStatus, type SaveState } from '@/components/settings/SaveStatus';
import { useAnswerStyle } from '@/hooks/useAnswerStyle';
import useAnswerStyleActions, { type AnswerStyleSaveResult } from '@/hooks/useAnswerStyleActions';
import { BORDER_RADIUS, primitiveColors, semanticColors } from '@/theme/tokens';

const SAVED_VISIBLE_MS = 2400;
const SAVED_KEY: Record<AnswerStyleSaveResult, string> = {
  account: 'answerStyle.note.accountSaved',
  browser: 'answerStyle.note.browserSaved',
  memory: 'answerStyle.note.visitOnly',
};

const FOCUS_VISIBLE = {
  '&:focus-visible': {
    outline: `2px solid ${semanticColors.brandPrimary}`,
    outlineOffset: '2px',
  },
} as const;

/**
 * Settings → Answer style. Same controls and example as the onboarding dialog;
 * edits stay a draft until "Save changes" succeeds, and "Cancel" restores the
 * saved choice. A successful save shapes future answers only — nothing already
 * in a conversation is rewritten. Mirrors the mobile section.
 */
export default function AnswerStyleSettingsSection() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const snapshot = useAnswerStyle();
  const { save, signedIn } = useAnswerStyleActions();
  const [draft, setDraft] = useState<AnswerStyle>(snapshot.style);
  const [state, setState] = useState<SaveState>('idle');
  const [savedKey, setSavedKey] = useState(SAVED_KEY.account);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Follow the saved value while the voter is not mid-edit — a hydration or a
  // save from the composer lands here — without discarding an unsaved draft.
  const [lastSaved, setLastSaved] = useState(snapshot.style);
  if (!sameAnswerStyle(lastSaved, snapshot.style)) {
    setLastSaved(snapshot.style);
    if (sameAnswerStyle(draft, lastSaved)) setDraft(snapshot.style);
  }

  useEffect(
    () => () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    },
    []
  );

  const ready = snapshot.hydrated && (snapshot.scope === 'account') === signedIn;
  const dirty = !sameAnswerStyle(draft, snapshot.style);
  const saving = state === 'saving';

  const handleSave = async () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setState('saving');
    try {
      const result = await save(draft, 'settings');
      setSavedKey(SAVED_KEY[result]);
      setState('saved');
      savedTimer.current = setTimeout(() => setState('idle'), SAVED_VISIBLE_MS);
    } catch {
      setState('error');
    }
  };

  let note: string;
  if (!signedIn && snapshot.guestStorage === 'memory') {
    note = tc('answerStyle.note.visitOnly');
  } else if (dirty || !snapshot.explicit) {
    note = signedIn ? tc('answerStyle.note.accountBefore') : tc('answerStyle.note.browserBefore');
  } else {
    note = signedIn ? tc('answerStyle.note.accountSaved') : tc('answerStyle.note.browserStored');
  }

  return (
    <Box
      component="section"
      aria-labelledby="settings-answer-style-label"
      sx={{
        border: `1px solid ${semanticColors.borderLight}`,
        borderRadius: BORDER_RADIUS,
        p: '20px',
        mb: '16px',
        backgroundColor: primitiveColors.white,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', mb: '16px' }}>
        <Box sx={{ mt: '2px', flexShrink: 0, lineHeight: 0 }}>
          <ChatBubbleLeftRightIcon
            style={{ width: 20, height: 20, color: semanticColors.textPrimary }}
            aria-hidden="true"
          />
        </Box>
        <Box>
          <Typography
            id="settings-answer-style-label"
            sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3 }}
          >
            {t('answerStyleTitle')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
            {t('answerStyleDesc')}
          </Typography>
        </Box>
      </Box>

      {!ready ? (
        <CircularProgress size={20} sx={{ color: semanticColors.brandPrimary }} />
      ) : (
        <>
          <AnswerStyleEditor value={draft} onChange={setDraft} />
          <Typography sx={{ mt: '16px', fontSize: 12, color: semanticColors.textTertiary }}>
            {note}
          </Typography>
          <Typography sx={{ mt: '4px', fontSize: 12, color: semanticColors.textTertiary }}>
            {tc('answerStyle.note.appliesNext')}
          </Typography>
          <Box
            sx={{
              mt: '16px',
              pt: '14px',
              borderTop: `1px solid ${semanticColors.borderLight}`,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <SaveStatus
              state={state}
              error={tc('answerStyle.error')}
              savingLabel={tc('answerStyle.actions.saving')}
              savedLabel={tc(savedKey)}
            />
            <Box sx={{ display: 'flex', gap: '4px', ml: 'auto' }}>
              {dirty && (
                <Button
                  onClick={() => {
                    setDraft(snapshot.style);
                    setState('idle');
                  }}
                  disabled={saving}
                  sx={{
                    textTransform: 'none',
                    fontSize: 13,
                    color: semanticColors.textSecondary,
                    minHeight: 40,
                    // Shorthand: the global MuiButton padding would otherwise win.
                    padding: '0 12px',
                    ...FOCUS_VISIBLE,
                  }}
                >
                  {tc('answerStyle.actions.cancel')}
                </Button>
              )}
              <Button
                onClick={() => void handleSave()}
                disabled={!dirty || saving}
                variant="contained"
                disableElevation
                startIcon={
                  saving ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : undefined
                }
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  fontWeight: 600,
                  minHeight: 40,
                  padding: '0 16px',
                  borderRadius: BORDER_RADIUS,
                  backgroundColor: semanticColors.textPrimary,
                  color: semanticColors.textOnPrimary,
                  '&:hover': { backgroundColor: semanticColors.hover },
                  '&.Mui-disabled': {
                    backgroundColor: semanticColors.disabledBackground,
                    color: semanticColors.textSecondary,
                  },
                  ...FOCUS_VISIBLE,
                }}
              >
                {tc('answerStyle.actions.save')}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
